import { useEffect, useMemo } from "react";

import { LoadQuoteValues } from "../../load-quote";
import { getQuoteFormLcvSliderDefaultTonnes } from "./fields/QuoteFormLcvCapacitySlider";
import { isQuoteFormValid } from "./validateQuoteForm";
import { QUOTE_FORM_LCV_LORRY_TYPE } from "./loadQuoteForm.constants";

type UseLoadQuoteFormOptions = {
    values: LoadQuoteValues;
    onChange: (patch: Partial<LoadQuoteValues>) => void;
    isRemoteConfigLoaded: boolean;
    t: (key: string, opts?: object) => string;
    /** When true, capacity/tonnes is not required (e.g. compact route step). */
    skipCapacityValidation?: boolean;
};

export function useLoadQuoteForm({
    values,
    onChange,
    isRemoteConfigLoaded,
    t,
    skipCapacityValidation = false
}: UseLoadQuoteFormOptions) {
    const validationOptions = useMemo(
        () => ({ isRemoteConfigLoaded, t, skipCapacityValidation }),
        [isRemoteConfigLoaded, skipCapacityValidation, t]
    );

    useEffect(() => {
        if (!isRemoteConfigLoaded || skipCapacityValidation) {
            return;
        }

        const patch: Partial<LoadQuoteValues> = {};

        if (values.lorryType !== QUOTE_FORM_LCV_LORRY_TYPE) {
            patch.lorryType = QUOTE_FORM_LCV_LORRY_TYPE;
        }

        if (!values.tonnes?.trim()) {
            patch.tonnes = getQuoteFormLcvSliderDefaultTonnes();
        }

        if (Object.keys(patch).length) {
            onChange(patch);
        }
    }, [isRemoteConfigLoaded, onChange, skipCapacityValidation, values.lorryType, values.tonnes]);

    const isFormValid = useMemo(() => isQuoteFormValid(values, validationOptions), [validationOptions, values]);

    return { isFormValid };
}
